import { useDrag, useDragLayer } from 'react-dnd';
import { WorkOrder } from '../../models/workOrder';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Popover,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Icons } from '../../assets/icons';
import { LoadingIndicator } from '../../components/loading-indicator.component';
import FormDateField, { DateFormat } from '../../components/hook-form/form-date.field.component';
import { useFormContext } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import { useSaveData } from '../../hooks/useSaveData';
import { workOrdersApiKeyStore } from '../../api/workorders-api-key-store';
import { useAuthorize } from '../../hooks/use-authorize';
import { UserPermissionName } from '../../models/user-permission-name';
import dayjs from 'dayjs';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';

interface InspectionsProps {
  inspections: WorkOrder[];
  handleInspectionsFilterClick: () => void;
}

const CustomDragPreview = ({ count }: { count: number }) => {
  return (
    <div
      style={{
        padding: '8px 12px',
        backgroundColor: '#007bff',
        color: '#fff',
        borderRadius: '8px',
        fontWeight: 'bold',
        boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
      }}
    >
      You’ve selected {count} {count === 1 ? 'item' : 'items'}
    </div>
  );
};

const CustomDragLayer = () => {
  const { item, isDragging, currentOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    isDragging: monitor.isDragging(),
    currentOffset: monitor.getClientOffset(),
  }));

  if (!isDragging || !currentOffset || !item?.inspections) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        pointerEvents: 'none',
        left: currentOffset.x,
        top: currentOffset.y,
        transform: 'translate(-50%, -50%)',
        zIndex: 1500,
      }}
    >
      <CustomDragPreview count={item.inspections.length} />
    </div>
  );
};

const InspectionsList = ({
  inspections,
  handleCalenderButtonClick,
}: {
  inspections: WorkOrder[];
  handleCalenderButtonClick: (woId: number) => void;
}) => {
  const [selectedInspections, setSelectedInspections] = useState<WorkOrder[]>([]);

  if (!inspections || inspections.length === 0) return <p style={{ textAlign: 'center' }}>No inspections available.</p>;

  return (
    <div className="flex flex-col gap-4 p-4" style={{ width: '100%' }}>
      <h3 style={{ fontSize: '20px', fontWeight: 'bold', padding: 0, marginBottom: '10px' }}>INSPECTIONS</h3>
      <CustomDragLayer />
      {inspections.map((inspection) => (
        <DraggableInspection
          key={inspection.workOrderNumber}
          inspection={inspection}
          handleCalenderButtonClick={handleCalenderButtonClick}
          selectedInspections={selectedInspections}
          setSelectedInspections={setSelectedInspections}
        />
      ))}
    </div>
  );
};

// Move useDrag into a separate component to avoid hooks-related errors
const DraggableInspection = ({
  inspection,
  handleCalenderButtonClick,
  selectedInspections,
  setSelectedInspections,
}: {
  inspection: WorkOrder;
  handleCalenderButtonClick: (woId: number) => void;
  selectedInspections: WorkOrder[];
  setSelectedInspections: React.Dispatch<React.SetStateAction<WorkOrder[]>>;
}) => {
  const ishavePermission = useAuthorize([UserPermissionName?.MANAGE_SCHEDULEBALANCING]);

  const isSelected = selectedInspections.some((i) => i.workOrderID === inspection.workOrderID);

  const toggleSelection = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;

    // Prevent toggling if click is on 'more-info-button' or inside the popover
    if (
      target.closest('.more-info-button') ||
      target.closest('.calendar-icon-button') ||
      target.closest('.MuiPopover-root') || // For MUI popover
      document.querySelector('.popOverInnererWrap')?.contains(target)
    ) {
      return;
    }

    if (isSelected) {
      setSelectedInspections((prev) => prev.filter((i) => i.workOrderID !== inspection.workOrderID));
    } else {
      setSelectedInspections((prev) => [...prev, inspection]);
    }
  };

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'inspection',
      // item: { workOrder: inspection },
      item: { inspections: selectedInspections.length > 0 ? selectedInspections : [inspection] },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [ishavePermission, selectedInspections] // Re-run if flag changes
  );

  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <div
        ref={ishavePermission ? drag : null}
        onClick={toggleSelection}
        style={{
          // opacity: isDragging ? 0.5 : 1,
          // cursor: 'grab',
          // backgroundColor: '#f2f3f5',
          marginBottom: 5,
          padding: '5px 15px',
          backgroundColor: isSelected ? '#e0f0ff' : '#f2f3f5',
          cursor: 'grab',
          opacity: isDragging ? 0.5 : 1,
          // padding: '10px',
          borderRadius: '8px',
          border: isSelected ? '2px solid #007bff' : '1px solid #ccc',
          userSelect: 'none',
        }}
        className="draggable-inspectionBlock bg-gray-100 bg-white shadow-md rounded-lg p-4 border border-gray-300 mb-4"
      >
        <Grid container>
          <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
            <small className="gray" style={{ color: '#787878' }}>
              Work Order:
            </small>
            <Typography className="font-semibold" style={{ color: '#000' }}>
              {!inspection?.workOrderNumber ? '-' : inspection?.workOrderNumber}
            </Typography>
          </Grid>
          <Grid item xl={4} lg={4} md={4} sm={4} xs={4} style={{ maxWidth: 'auto' }}>
            <small className="gray" style={{ color: '#787878' }}>
              Service:
            </small>{' '}
            <Typography variant="body1"> {!inspection?.serviceName ? '-' : inspection?.serviceName}</Typography>
          </Grid>
          <Grid item xl={2} lg={2} md={2} sm={2} xs={2} style={{ maxWidth: 'auto' }}>
            <small className="gray" style={{ color: '#787878' }}>
              Permit Number:
            </small>{' '}
            <Typography variant="body1"> {!inspection?.permitNumber ? '-' : inspection?.permitNumber}</Typography>
          </Grid>
          <Grid item xl={3} lg={3} md={3} sm={3} xs={3} style={{ marginLeft: 'auto' }}>
            <small className="gray" style={{ color: '#787878' }}>
              Proposed Schedule Date:
            </small>
            <Typography variant="body1">
              <span style={{ color: inspection?.isPastProposedScheduledDate ? 'red' : 'black', marginRight: 10 }}>
                {inspection?.proposedScheduledDate}
              </span>
              <IconButton
                className="calendar-icon-button"
                aria-label="move"
                onClick={() => handleCalenderButtonClick(inspection?.workOrderID)}
                style={{ padding: 0, marginLeft: 0 }}
              >
                <Tooltip title={<Typography variant="body1">Change proposed schedule date</Typography>}>
                  <img style={{ width: 12 }} src={Icons.IconCalendar} alt="change proposed schedule date" />
                </Tooltip>
              </IconButton>
            </Typography>
          </Grid>
          <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
            <small className="gray" style={{ color: '#787878' }}>
              Scheduled Date:
            </small>
            <Typography>{!inspection?.scheduledDate ? '-' : inspection?.scheduledDate}</Typography>
          </Grid>
          <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
            <small className="gray" style={{ color: '#787878' }}>
              Inspector:
            </small>
            <Typography>{!inspection?.inspectorName ? '-' : inspection?.inspectorName}</Typography>
          </Grid>

          <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
            <small className="gray" style={{ color: '#787878' }}>
              Status:
            </small>
            <Typography>{!inspection?.workOrderStatus ? '-' : inspection?.workOrderStatus}</Typography>
          </Grid>

          <Grid item xl={3} lg={3} md={3} sm={3} xs={3} style={{ marginTop: 8 }}>
            <small className="gray" style={{ color: '#787878' }}>
              Address:
            </small>
            <Typography>{!inspection?.address ? '-' : inspection?.address}</Typography>
          </Grid>
          <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
            <small className="gray" style={{ color: '#787878' }}>
              Project Name:
            </small>
            <Typography>{!inspection?.projectName ? '-' : inspection?.projectName}</Typography>
          </Grid>
          <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
            <small className="gray" style={{ color: '#787878' }}>
              Lot #:
            </small>
            <Typography>{!inspection?.unitName ? '-' : inspection?.unitName}</Typography>
          </Grid>
          <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
            <small className="gray" style={{ color: '#787878' }}>
              City:
            </small>
            <Typography>{!inspection?.city ? '-' : inspection?.city}</Typography>
          </Grid>
          <Grid item xl={3} lg={3} md={3} sm={3} xs={3} style={{ textAlign: 'right', display: 'flex' }}>
            <Typography
              onClick={handlePopoverOpen}
              style={{
                cursor: 'pointer',
                color: '#007bff',

                display: 'inline-flex',
                alignItems: 'center',
                marginLeft: 'auto',
              }}
            >
              <button
                className="btn btn-sm btn-dark more-info-button"
                style={{ lineHeight: '1', cursor: 'pointer', marginLeft: 'auto' }}
              >
                More Info
              </button>
            </Typography>
          </Grid>
        </Grid>

        <Popover
          className="popOverOuterWrap"
          open={open}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <div className="popOverInnererWrap">
            <Typography
              variant="body1"
              className="d-flex"
              style={{
                alignItems: 'center',
                justifyContent: 'center',
                borderBottom: '1px solid #ccc',
                paddingBottom: '5px',
              }}
            >
              <small>Customer: </small> {inspection?.customerName || ''}
            </Typography>
            <Typography variant="body1" className="d-flex">
              <small>Jobsite: </small> {inspection?.jobsite || ''}
            </Typography>

            <Typography variant="body1" className="d-flex">
              <small>Builder Job Number: </small> {inspection?.jobNumber || ''}
            </Typography>
            <Typography variant="body1" className="d-flex">
              <small>Office: </small> {inspection?.officeName || ''}
            </Typography>

            <Typography variant="body1" className="d-flex">
              <small>Project Code: </small> {inspection?.projectCode || ''}
            </Typography>
            <Typography variant="body1" className="d-flex">
              <small>Cut-off Time: </small> {inspection?.cutOffTime || ''}
            </Typography>
            <Typography variant="body1" className="d-flex">
              <small>Square Feet: </small> {inspection?.squareFeet || ''}
            </Typography>

            <Typography variant="body1" className="d-flex">
              <small>Zip: </small> {inspection?.zip || ''}
            </Typography>
          </div>
        </Popover>
      </div>
    </>
  );
};

const Inspections = ({ inspections, handleInspectionsFilterClick }: InspectionsProps) => {
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [currentWorkOrderID, setCurrentWorkOrderID] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const { setValue, getValues, watch } = useFormContext();

  const [pendingRuleResponse, setPendingErrorRoles] = useState<any>();
  const [openRuleDialog, setRuleDialog] = useState<boolean>(false);

  const ishavePermission = useAuthorize([UserPermissionName?.OVERRIDE]);

  const { isLoading: isLoadingProposedSheduleDate, mutateAsync: updateProposedSheduleDate } = useSaveData<any>(
    workOrdersApiKeyStore.updateProposedScheduleDate()
  );

  const { isLoading: isPrerequisitesLoading, mutateAsync: mutatePrerequisitesAsync } = useSaveData<any>(workOrdersApiKeyStore.checkPrerequisitesBeforeReady());

  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleItemsPerPageChange = (event: any) => {
    setItemsPerPage(event.target.value as number);
    setPage(1);
  };

  const validInspections = Array.isArray(inspections) ? inspections : [];

  const startIndex = (page - 1) * itemsPerPage;
  const paginatedInspections = validInspections.slice(startIndex, startIndex + itemsPerPage);

  const handleCalenderButtonClick = (WoId: number) => {
    setCurrentWorkOrderID(WoId);
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setValue('proposedSheduleDate', undefined);
    setCurrentWorkOrderID(0);
  };

  const handleSumbitConfirm = async () => {
    const rulePayload = {
      workOrderID: currentWorkOrderID,
      proposedScheduleDate: dayjs(watch('proposedSheduleDate'))?.format('MM/DD/YYYY')
    }
    const ruleResponse: any = await mutatePrerequisitesAsync({ data: rulePayload });

    const hasPendingRule = ruleResponse?.data?.pendingRules?.length;
    if (hasPendingRule > 0) {
      setPendingErrorRoles(ruleResponse?.data)
      setRuleDialog(true);
    }
    else {
      updateProposedSheduleDate({
        data: {
          workOrderID: currentWorkOrderID,
          proposedScheduledDate: watch('proposedSheduleDate'),
        },
      }).then((data: any) => {
        handleInspectionsFilterClick();
        closeDialog();
      });
    }
  };

  const closeValidationDialog = () => {
    setRuleDialog(false);
    setPendingErrorRoles(undefined);
  }

  const handleSumbitRuleConfirm = () => {
    closeValidationDialog();
    updateProposedSheduleDate({
      data: {
        workOrderID: currentWorkOrderID,
        proposedScheduledDate: watch('proposedSheduleDate'),
      },
    }).then((data: any) => {
      handleInspectionsFilterClick();
      closeDialog();
    });
  }

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <div>
      <InspectionsList inspections={paginatedInspections} handleCalenderButtonClick={handleCalenderButtonClick} />
      {openDialog && (
        <Dialog
          open={openDialog}
          fullWidth={true}
          maxWidth={'sm'}
        // onClose={onClosed}
        >
          <DialogTitle id="scroll-dialog-title">Proposed Schedule Date</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={closeDialog}
            sx={{
              position: 'absolute',
              right: 10,
              top: 15,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Tooltip title={<Typography variant="body1">Close</Typography>}>
              <CloseIcon />
            </Tooltip>
          </IconButton>

          {isLoadingProposedSheduleDate && <LoadingIndicator isLoading={isLoadingProposedSheduleDate} />}
          <DialogContent dividers className="popupDilog">
            <Grid container spacing={2}>
              <Grid item lg={12} sm={12} xl={12} xs={12}>
                <FormDateField
                  label="Proposed Schedule Date"
                  name="proposedSheduleDate"
                  dateLimits={{ minDateEnabled: true }}
                  format={DateFormat.SIMPLE_DATE}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <div style={{ padding: '5px', paddingLeft: '16px', paddingRight: '16px' }}>
            <DialogActions className="spaceBetween">
              <Button
                type="button"
                variant='contained'
                // className={`btn btn-sm ${!getValues('proposedSheduleDate') ? '' : 'btn-primary'}`}
                style={{ width: '100px', marginLeft: 'auto' }}
                onClick={handleSumbitConfirm}
                disabled={!watch('proposedSheduleDate') || isLoadingProposedSheduleDate || isPrerequisitesLoading || isReadOnlyPermission}
              >
                Save
              </Button>
            </DialogActions>
          </div>
        </Dialog>
      )}
      {validInspections.length > 0 && (
        <Grid
          padding={10}
          style={{
            padding: '20px 0px 15px',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <Pagination
            count={Math.ceil(validInspections.length / itemsPerPage)}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            shape="rounded"
          />
          <FormControl className="pagingperSelect" variant="outlined">
            <Select
              labelId="items-per-page-label"
              id="items-per-page-select"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
            <span>items per page</span>
          </FormControl>
        </Grid>
      )}

      {openRuleDialog && (
        <Dialog
          open={openRuleDialog}
          className="modalpopup"
          title={'CONFIRM STATUS'}
        // onClose={closeDialog}
        >
          <div>
            <div style={{ padding: '25px 50px' }}>
              <div>
                <h4>Please see the below validation</h4>
                <br></br>
                <ul>
                  {pendingRuleResponse?.pendingRules?.map((rules: any) => (
                    <div key={`${pendingRuleResponse?.workorderID}-${rules}`}>
                        {/* <li style={{ marginLeft: '25px' }}> */}
                        <span>{rules}</span>
                        {/* </li> */}
                      {rules.includes("pre-requisite") && pendingRuleResponse?.preReqWorkOrders?.length > 0 && (
                        <dl>
                          <dt>Pending Work Orders:</dt>
                          {pendingRuleResponse?.preReqWorkOrders?.map((data: any) => <dd key={data}>- {data}</dd>)}
                        </dl>
                      )}
                    </div>
                  ))}
                </ul>
              </div>
              <br></br>
              <div>
                {ishavePermission ? (
                  <h5>
                    You have permission to change the proposed schedule date without completing the validation. 
                    Are you sure you want to
                    continue?
                  </h5>
                ) : (
                  <h5>You can not change the proposed schedule date without completing the validation.</h5>
                )}
              </div>
              {/* <LoadingIndicator isLoading={isLoading} /> */}
            </div>
          </div>
          <DialogActionsBar>
            <div className=" spaceBetween">
              <button
                style={{ cursor: 'pointer' }}
                className="btn btn-sm btn-default btn-lightGrey"
                onClick={closeValidationDialog}
                autoFocus
              >
                Cancel
              </button>
              {ishavePermission && (
                <button
                  className="btn btn-sm btn-primary  btn-delete"
                  type="button"
                  onClick={handleSumbitRuleConfirm}
                  // disabled={isLoading}
                  style={{ cursor: 'pointer' }}
                >
                  Confirm
                </button>
              )}
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default Inspections;
